import { persistentAtom } from '@nanostores/persistent'
import { computed, type Store } from 'nanostores'
import type { ErrorLevel } from '$types/grammar'
import { generateAIProviders } from '$lib/utils/modelUtils'

// Type definitions for settings
export interface EditorSettings {
  lineHeight: number
  wordWrap: boolean
  autoSave: boolean
  autoSaveInterval: number  // milliseconds
  theme: 'light' | 'dark' | 'auto'
  indentSize: number
  showInvisibles: boolean
  enableMarkdown: boolean
  fontSize: 'small' | 'medium' | 'large' | 'xlarge' | 'custom'  // Font size preset
  customFontSize?: number  // Custom font size in pixels (12-24)
  fontFamily: 'system-default' | 'noto-sans-kr' | 'noto-serif-kr' | 'gowun-batang' | 'gowun-dodum' | 'nanum-gothic' | 'nanum-myeongjo' | 'ibm-plex-sans-kr' | 'georgia' | 'sf-mono' | 'consolas' | 'monaco'  // Font family
}

export interface UISettings {
  showToolbar: boolean
  showSidebar: boolean
  showLineNumbers: boolean
  showStatistics: boolean
  showMinimap: boolean
  sidebarPosition: 'left' | 'right'
  compactMode: boolean
  language: 'ko' | 'en'
  animations: boolean
}

export interface CheckerSettings {
  enableSpelling: boolean
  enableGrammar: boolean
  enableStyle: boolean
  checkOnType: boolean
  debounceMs: number
  errorLevels: ErrorLevel[]
  maxSuggestions: number
  ignoreNumbers: boolean
  ignoreUrls: boolean
  ignoreEmails: boolean
}

// API Settings
export interface ApiSettings {
  // Proxy server URL (for local cache server)
  proxyUrl?: string
  // Default servers (hidden from UI)
  defaultServers?: {
    primary: {
      server: string
      port: string
      protocol: 'http' | 'https'
      apiKey?: string
    }
    secondary?: {
      server: string
      port: string
      protocol: 'http' | 'https'
      apiKey?: string
    }
  }
  // User custom server (visible in UI)
  customServer?: {
    enabled: boolean
    url: string
    apiKey?: string
    providerType?: 'bareun' | 'naraSpeller'  // Provider type selection
    weakOpt?: 0 | 1  // NaraSpeller specific option
  }
  // Current active server (computed)
  server: string
  port: string
  protocol: 'http' | 'https'
  apiKey?: string
  // Subscription tier
  tier?: 'free' | 'trial' | 'premium'
}

// AI Provider Settings
export interface AIProvider {
  id: string
  name: string
  icon: string
  enabled: boolean
  apiKey: string
  baseUrl?: string
  models?: string[]
  defaultModel?: string
  hidden?: boolean  // Add hidden flag for providers that shouldn't show in UI
}

// AI Settings for the AI analysis service
export interface AISettings {
  enabled: boolean
  provider: string
  openaiApiKey: string
  anthropicApiKey: string
  googleApiKey: string
  upstageApiKey: string
  openrouterApiKey: string
  cerebrasApiKey: string
  groqApiKey: string
  selectedModel: string
  contextWindow: number
  showTokenWarning: boolean
  tokenWarningThreshold: number
  maxTokens: number
  temperature: number
  isPremium?: boolean
  isTrial?: boolean
}

export interface UnifiedCheckSettings {
  grammarEnabled: boolean
  aiEnabled: boolean
  aiModel?: string  // Separate AI model for grammar check
}

export interface AppSettings {
  editor: EditorSettings
  ui: UISettings
  checker: CheckerSettings
  api: ApiSettings
  aiProviders: AIProvider[]
  ai?: AISettings  // AI analysis settings
  unifiedCheck?: UnifiedCheckSettings  // Unified check button settings
}

// Default settings
export const defaultSettings: AppSettings = {
  editor: {
    lineHeight: 1.6,
    wordWrap: true,
    autoSave: true,
    autoSaveInterval: 30000, // 30 seconds
    theme: 'auto',
    indentSize: 2,
    showInvisibles: false,
    enableMarkdown: true,
    fontSize: 'medium',  // Default to medium (14px)
    customFontSize: undefined,  // Only set when using custom size
    fontFamily: 'system-default'  // Default font family
  },
  ui: {
    showToolbar: true,
    showSidebar: true,
    showLineNumbers: true,
    showStatistics: true,
    showMinimap: false,
    sidebarPosition: 'left',
    compactMode: false,
    language: 'ko',
    animations: true
  },
  checker: {
    enableSpelling: true,
    enableGrammar: true,
    enableStyle: true,
    checkOnType: false,  // Disable automatic checking on type
    debounceMs: 500,
    errorLevels: ['error', 'warning', 'suggestion'],
    maxSuggestions: 5,
    ignoreNumbers: false,
    ignoreUrls: true,
    ignoreEmails: true
  },
  api: {
    // Proxy server handles all API access (no direct server access needed)
    proxyUrl: import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org',
    // Legacy settings (deprecated - kept for backward compatibility only)
    defaultServers: {
      primary: {
        server: 'proxy',
        port: '443',
        protocol: 'https',
        apiKey: '' // No API key needed - proxy handles authentication
      },
      secondary: {
        server: 'proxy',
        port: '443',
        protocol: 'https',
        apiKey: '' // No API key needed - proxy handles authentication
      }
    },
    // User custom server (disabled - proxy is required)
    customServer: {
      enabled: false,
      url: '',
      apiKey: ''
    },
    // Current active server (proxy handles everything)
    server: 'proxy',
    port: '443',
    protocol: 'https',
    apiKey: '', // No API key needed for proxy
    // Default to free tier
    tier: 'free'
  },
  ai: {
    enabled: true,  // Enable by default since OpenRouter is free
    provider: 'openrouter',
    openaiApiKey: '',
    anthropicApiKey: '',
    googleApiKey: '',
    upstageApiKey: '',
    openrouterApiKey: '',
    cerebrasApiKey: '',
    groqApiKey: '',
    selectedModel: 'openai/gpt-oss-120b',  // OpenRouter's free model as default
    contextWindow: 128000,
    showTokenWarning: true,
    tokenWarningThreshold: 0.8,
    maxTokens: 2000,
    temperature: 0.3,
    isPremium: false,
    isTrial: false
  },
  aiProviders: generateAIProviders(),
  unifiedCheck: {
    grammarEnabled: true,
    aiEnabled: true,  // Enable AI analysis by default
    aiModel: 'openai/gpt-oss-120b'  // Set default AI model for unified check
  }
}

// Create persistent store with Nanostores
// In test environment, we use a simple atom instead of persistent
const isTestEnvironment = typeof process !== 'undefined' && process.env?.VITEST

export const settingsStore = isTestEnvironment
  ? (() => {
      // For tests, create a simple store that mimics persistentAtom
      let store = { ...defaultSettings }
      const listeners = new Set<(value: AppSettings) => void>()
      
      return {
        get: () => {
          // Update model lists from config for consistency
          const updatedProviders = store.aiProviders.map((p: AIProvider) => {
            const configProvider = defaultSettings.aiProviders.find(dp => dp.id === p.id)
            if (configProvider) {
              return { ...p, models: configProvider.models }
            }
            return p
          })
          // Ensure AI settings have default selectedModel
          const aiSettings = store.ai || defaultSettings.ai
          if (aiSettings && !aiSettings.selectedModel) {
            aiSettings.selectedModel = 'openai/gpt-oss-120b'
          }
          return { ...store, aiProviders: updatedProviders, ai: aiSettings }
        },
        set: (value: AppSettings) => {
          store = value
          // Save to localStorage in tests
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('korean-grammar:settings', JSON.stringify(value))
          }
          listeners.forEach(fn => fn(value))
        },
        subscribe: (fn: (value: AppSettings) => void) => {
          listeners.add(fn)
          fn(store)
          return () => listeners.delete(fn)
        }
      }
    })()
  : persistentAtom<AppSettings>(
      'korean-grammar:settings',
      defaultSettings,
      {
        encode: JSON.stringify,
        decode: (value: string) => {
          try {
            const parsed = JSON.parse(value)
            // Merge with defaults to ensure all fields exist
            // For aiProviders, ensure OpenRouter has hidden flag
            // Also update model lists from config
            const providers = (parsed && parsed.aiProviders) || defaultSettings.aiProviders
            const updatedProviders = providers.map((p: AIProvider) => {
              if (p.id === 'openrouter') {
                return { ...p, hidden: true, enabled: true }
              }
              // Update model list from config for each provider
              const configProvider = defaultSettings.aiProviders.find(dp => dp.id === p.id)
              if (configProvider) {
                return { ...p, models: configProvider.models }
              }
              return p
            })
            
            // Ensure AI is enabled for OpenRouter (no API key needed)
            const aiSettings = { 
              ...defaultSettings.ai, 
              ...(parsed?.ai || {}) 
            }
            
            // Always ensure selectedModel has a value
            if (!aiSettings.selectedModel) {
              aiSettings.selectedModel = 'openai/gpt-oss-120b'
            }
            
            // Force enable if OpenRouter is available and not already enabled
            if (!parsed?.ai || !parsed.ai.enabled) {
              aiSettings.enabled = true
              aiSettings.provider = 'openrouter'
            }
            
            // Preserve unifiedCheck settings properly
            const unifiedCheckSettings = parsed?.unifiedCheck ?
              { ...defaultSettings.unifiedCheck, ...parsed.unifiedCheck } :
              defaultSettings.unifiedCheck

            return {
              editor: { ...defaultSettings.editor, ...(parsed?.editor || {}) },
              ui: { ...defaultSettings.ui, ...(parsed?.ui || {}) },
              checker: { ...defaultSettings.checker, ...(parsed?.checker || {}) },
              api: { ...defaultSettings.api, ...(parsed?.api || {}) },
              aiProviders: updatedProviders,
              ai: aiSettings,
              unifiedCheck: unifiedCheckSettings
            }
          } catch {
            return defaultSettings
          }
        }
      }
    )

// Computed stores for specific sections
export const editorSettingsStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.editor
)

export const uiSettingsStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.ui
)

export const checkerSettingsStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.checker
)

export const apiSettingsStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.api
)

export const aiProvidersStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.aiProviders
)

export const aiSettingsStore = computed(
  settingsStore as Store<AppSettings>,
  (settings: AppSettings) => settings.ai || defaultSettings.ai
)

// Font size mappings
export const fontSizeMap = {
  small: 14,
  medium: 16,
  large: 18,
  xlarge: 20
} as const

// Get actual font size in pixels
export function getEditorFontSize(settings: EditorSettings): number {
  if (settings.fontSize === 'custom' && settings.customFontSize) {
    // Clamp custom font size between 12 and 24
    return Math.max(12, Math.min(24, settings.customFontSize))
  }
  return fontSizeMap[settings.fontSize as keyof typeof fontSizeMap] || 14
}

// Font family mapping
const fontFamilyMap = {
  'system-default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  'noto-sans-kr': '"Noto Sans KR", -apple-system, sans-serif',
  'noto-serif-kr': '"Noto Serif KR", Georgia, serif',
  'gowun-batang': '"Gowun Batang", serif',
  'gowun-dodum': '"Gowun Dodum", sans-serif',
  'nanum-gothic': '"Nanum Gothic", sans-serif',
  'nanum-myeongjo': '"Nanum Myeongjo", serif',
  'ibm-plex-sans-kr': '"IBM Plex Sans KR", sans-serif',
  'georgia': 'Georgia, serif',
  'sf-mono': '"SF Mono", ui-monospace, monospace',
  'consolas': 'Consolas, "Courier New", monospace',
  'monaco': 'Monaco, "Courier New", monospace'
} as const

// Update CSS variables based on font size
export function updateFontSizeVariables(fontSize: number): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.style.setProperty('--editor-font-size', `${fontSize}px`)

  // Adjust line height based on font size
  const lineHeight = fontSize <= 14 ? 1.6 : fontSize <= 16 ? 1.65 : 1.7
  root.style.setProperty('--editor-line-height', String(lineHeight))
}

// Update CSS variables based on font family
export function updateFontFamilyVariables(fontFamily: EditorSettings['fontFamily']): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const fontFamilyValue = fontFamilyMap[fontFamily] || fontFamilyMap['system-default']
  root.style.setProperty('--editor-font-family', fontFamilyValue)
}

// Action functions for settings manipulation
export const settingsActions = {
  // Editor settings
  updateEditor(updates: Partial<EditorSettings>) {
    const newSettings = {
      ...settingsStore.get(),
      editor: {
        ...settingsStore.get().editor,
        ...updates
      }
    }
    settingsStore.set(newSettings)

    // Update CSS variables if font size changed
    if (updates.fontSize !== undefined || updates.customFontSize !== undefined) {
      const fontSize = getEditorFontSize(newSettings.editor)
      updateFontSizeVariables(fontSize)
    }

    // Update CSS variables if font family changed
    if (updates.fontFamily !== undefined) {
      updateFontFamilyVariables(updates.fontFamily)
    }
  },
  
  toggleAutoSave() {
    const current = settingsStore.get()
    settingsActions.updateEditor({
      autoSave: !current.editor.autoSave
    })
  },
  
  setAutoSaveInterval(interval: number) {
    settingsActions.updateEditor({
      autoSaveInterval: Math.max(5000, Math.min(300000, interval)) // 5s to 5min
    })
  },
  
  // Font size actions
  setFontSize(size: 'small' | 'medium' | 'large' | 'xlarge' | 'custom') {
    settingsActions.updateEditor({ fontSize: size })
  },
  
  setCustomFontSize(size: number) {
    settingsActions.updateEditor({
      fontSize: 'custom',
      customFontSize: Math.max(12, Math.min(24, size))
    })
  },
  
  increaseFontSize() {
    const current = settingsStore.get().editor
    const currentSize = getEditorFontSize(current)
    
    if (currentSize <= 12) {
      settingsActions.setFontSize('medium')  // 12 -> 14
    } else if (currentSize <= 14) {
      settingsActions.setFontSize('large')   // 14 -> 16
    } else if (currentSize <= 16) {
      settingsActions.setFontSize('xlarge')  // 16 -> 18
    } else if (currentSize < 24) {
      settingsActions.setCustomFontSize(currentSize + 2)  // 18+ -> custom
    }
  },
  
  decreaseFontSize() {
    const current = settingsStore.get().editor
    const currentSize = getEditorFontSize(current)
    
    if (currentSize > 18) {
      // Custom size, decrease by 2
      settingsActions.setCustomFontSize(currentSize - 2)
    } else if (currentSize === 18) {
      // xlarge -> large
      settingsActions.setFontSize('large')
    } else if (currentSize >= 16) {
      // large -> medium
      settingsActions.setFontSize('medium')
    } else if (currentSize >= 14) {
      // medium -> small
      settingsActions.setFontSize('small')
    }
  },
  
  // UI settings
  updateUI(updates: Partial<UISettings>) {
    settingsStore.set({
      ...settingsStore.get(),
      ui: {
        ...settingsStore.get().ui,
        ...updates
      }
    })
  },
  
  toggleToolbar() {
    const current = settingsStore.get()
    settingsActions.updateUI({
      showToolbar: !current.ui.showToolbar
    })
  },
  
  toggleSidebar() {
    const current = settingsStore.get()
    settingsActions.updateUI({
      showSidebar: !current.ui.showSidebar
    })
  },
  
  toggleStatistics() {
    const current = settingsStore.get()
    settingsActions.updateUI({
      showStatistics: !current.ui.showStatistics
    })
  },
  
  setLanguage(language: 'ko' | 'en') {
    settingsActions.updateUI({ language })
  },
  
  // Checker settings
  updateChecker(updates: Partial<CheckerSettings>) {
    settingsStore.set({
      ...settingsStore.get(),
      checker: {
        ...settingsStore.get().checker,
        ...updates
      }
    })
  },
  
  toggleCheckType(type: 'spelling' | 'grammar' | 'style') {
    const current = settingsStore.get()
    const key = `enable${type.charAt(0).toUpperCase()}${type.slice(1)}` as keyof CheckerSettings
    settingsActions.updateChecker({
      [key]: !current.checker[key]
    })
  },
  
  setCheckMode(mode: 'auto' | 'manual') {
    settingsActions.updateChecker({
      checkOnType: mode === 'auto'
    })
  },
  
  setErrorLevels(levels: ErrorLevel[]) {
    settingsActions.updateChecker({
      errorLevels: levels
    })
  },
  
  // API settings
  updateApiSettings(updates: Partial<ApiSettings>) {
    const current = settingsStore.get()
    const api = current.api
    
    // Handle custom server updates
    if (updates.customServer !== undefined) {
      // If enabling custom server, parse the URL and update active server
      if (updates.customServer.enabled && updates.customServer.url) {
        try {
          const url = new URL(updates.customServer.url)
          updates.server = url.hostname
          updates.port = url.port || '443'
          updates.protocol = url.protocol.replace(':', '') as 'http' | 'https'
          updates.apiKey = updates.customServer.apiKey || api.apiKey
        } catch (e) {
          console.error('Invalid custom server URL:', e)
        }
      } else if (!updates.customServer.enabled) {
        // Revert to default server
        const defaultServer = api.defaultServers?.primary || defaultSettings.api.defaultServers!.primary
        updates.server = defaultServer.server
        updates.port = defaultServer.port
        updates.protocol = defaultServer.protocol
        updates.apiKey = defaultServer.apiKey
      }
    }
    
    settingsStore.set({
      ...current,
      api: {
        ...api,
        ...updates
      }
    })
  },
  
  getApiUrl(): string {
    const api = settingsStore.get().api
    
    // Use custom server if enabled
    if (api.customServer?.enabled && api.customServer.url) {
      return api.customServer.url
    }
    
    // Otherwise use the active server (which defaults to primary)
    const { protocol, server, port } = api
    return `${protocol}://${server}:${port}`
  },
  
  // Get current API key (from custom or default)
  getApiKey(): string {
    const api = settingsStore.get().api
    
    if (api.customServer?.enabled && api.customServer.apiKey) {
      return api.customServer.apiKey
    }
    
    return api.apiKey || ''
  },
  
  // Check if using custom server
  isUsingCustomServer(): boolean {
    return settingsStore.get().api.customServer?.enabled || false
  },
  
  // Get subscription tier
  getSubscriptionTier(): 'free' | 'trial' | 'premium' {
    return settingsStore.get().api.tier || 'free'
  },
  
  // Set subscription tier
  setSubscriptionTier(tier: 'free' | 'trial' | 'premium') {
    settingsActions.updateApiSettings({ tier })
  },
  
  // AI Provider settings
  updateAIProvider(providerId: string, updates: Partial<AIProvider>) {
    const settings = settingsStore.get()
    const providers = settings.aiProviders.map(provider => 
      provider.id === providerId 
        ? { ...provider, ...updates }
        : provider
    )
    
    // Update AI settings when provider changes
    const enabledProvider = providers.find(p => p.enabled && (p.apiKey || p.id === 'openrouter'))
    let aiSettings: AISettings = { ...defaultSettings.ai, ...(settings.ai || {}) } as AISettings
    
    if (enabledProvider) {
      // Map provider API keys to AI settings
      const keyMap: Record<string, keyof AISettings> = {
        'openai': 'openaiApiKey',
        'anthropic': 'anthropicApiKey',
        'google': 'googleApiKey',
        'upstage': 'upstageApiKey',
        'openrouter': 'openrouterApiKey'
      }
      
      const aiKeyField = keyMap[enabledProvider.id]
      if (aiKeyField) {
        aiSettings = {
          ...defaultSettings.ai,
          ...aiSettings,
          enabled: true,
          provider: enabledProvider.id,
          [aiKeyField]: enabledProvider.apiKey || '',
          selectedModel: enabledProvider.defaultModel || aiSettings.selectedModel || defaultSettings.ai?.selectedModel || ''
        } as AISettings
      }
    } else {
      // No provider enabled, disable AI and fallback to OpenRouter
      const openrouterProvider = providers.find(p => p.id === 'openrouter')
      if (openrouterProvider) {
        aiSettings = {
          ...defaultSettings.ai,
          ...aiSettings,
          enabled: false, // Keep disabled as no provider is explicitly enabled
          provider: 'openrouter',
          selectedModel: openrouterProvider.defaultModel || defaultSettings.ai?.selectedModel || 'openai/gpt-oss-120b'
        } as AISettings
      } else {
        aiSettings = { ...defaultSettings.ai, ...aiSettings, enabled: false }
      }
    }
    
    settingsStore.set({
      ...settings,
      aiProviders: providers,
      ai: aiSettings
    })
  },
  
  // Update AI Providers
  updateAIProviders(providers: AIProvider[]) {
    const settings = settingsStore.get()
    settingsStore.set({
      ...settings,
      aiProviders: providers
    })
  },
  
  // Update AI settings directly
  updateAISettings(updates: Partial<AISettings>) {
    const settings = settingsStore.get()
    settingsStore.set({
      ...settings,
      ai: {
        ...defaultSettings.ai,
        ...settings.ai,
        ...updates
      } as AISettings
    })
  },
  
  getEnabledAIProvider(): AIProvider | undefined {
    return settingsStore.get().aiProviders.find(p => 
      p.enabled && (p.apiKey || p.id === 'openrouter')
    )
  },
  
  getAIProviders(): AIProvider[] {
    return settingsStore.get().aiProviders
  },
  
  // Unified check settings
  updateUnifiedCheck(updates: Partial<UnifiedCheckSettings>) {
    const current = settingsStore.get()
    const newSettings = {
      ...current,
      unifiedCheck: {
        ...current.unifiedCheck || defaultSettings.unifiedCheck,
        grammarEnabled: updates.grammarEnabled ?? (current.unifiedCheck?.grammarEnabled ?? defaultSettings.unifiedCheck!.grammarEnabled),
        aiEnabled: updates.aiEnabled ?? (current.unifiedCheck?.aiEnabled ?? defaultSettings.unifiedCheck!.aiEnabled),
        aiModel: updates.aiModel !== undefined ? updates.aiModel : current.unifiedCheck?.aiModel
      }
    }
    settingsStore.set(newSettings)
  },
  
  // General actions
  reset() {
    settingsStore.set(defaultSettings)
  },
  
  exportSettings(): string {
    return JSON.stringify(settingsStore.get(), null, 2)
  },
  
  importSettings(json: string) {
    try {
      const imported = JSON.parse(json) as AppSettings

      // Validate and merge with defaults
      settingsStore.set({
        editor: { ...defaultSettings.editor, ...imported.editor },
        ui: { ...defaultSettings.ui, ...imported.ui },
        checker: { ...defaultSettings.checker, ...imported.checker },
        api: { ...defaultSettings.api, ...imported.api },
        aiProviders: imported.aiProviders || defaultSettings.aiProviders,
        ai: imported.ai ? { ...defaultSettings.ai, ...imported.ai } : defaultSettings.ai,
        unifiedCheck: imported.unifiedCheck ? { ...defaultSettings.unifiedCheck, ...imported.unifiedCheck } : defaultSettings.unifiedCheck
      })
    } catch (error) {
      console.error('Failed to import settings:', error)
    }
  },
  
  loadFromStorage() {
    // This triggers the persistent store to reload from localStorage
    // Useful when settings might have been changed in another window
    const stored = localStorage.getItem('korean-grammar:settings')
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as AppSettings
        settingsStore.set({
          editor: { ...defaultSettings.editor, ...parsed.editor },
          ui: { ...defaultSettings.ui, ...parsed.ui },
          checker: { ...defaultSettings.checker, ...parsed.checker },
          api: { ...defaultSettings.api, ...parsed.api },
          aiProviders: parsed.aiProviders || defaultSettings.aiProviders,
          ai: parsed.ai ? { ...defaultSettings.ai, ...parsed.ai } : defaultSettings.ai,
          unifiedCheck: parsed.unifiedCheck ? { ...defaultSettings.unifiedCheck, ...parsed.unifiedCheck } : defaultSettings.unifiedCheck
        })
      } catch {
        // If parsing fails, keep current settings
      }
    }
  }
}

// Subscribe to settings changes for cross-window sync
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'korean-grammar:settings' && e.newValue) {
      settingsActions.loadFromStorage()
    }
  })
}

// Helper to get current settings
export function getSettings(): AppSettings {
  return settingsStore.get()
}

// Helper to check if a specific error level is enabled
export function isErrorLevelEnabled(level: ErrorLevel): boolean {
  const settings = settingsStore.get()
  return settings.checker.errorLevels.includes(level)
}

// Helper to check if a specific check type is enabled
export function isCheckTypeEnabled(type: 'spelling' | 'grammar' | 'style'): boolean {
  const settings = settingsStore.get()
  const key = `enable${type.charAt(0).toUpperCase()}${type.slice(1)}` as keyof CheckerSettings
  return settings.checker[key] as boolean
}